import { formatRelative } from "../../lib/format";
import { useMembersIndex } from "../team/useMembersIndex";
import type { ActivityRow } from "./api";

const TYPE_ICONS: Record<string, string> = {
  note: "📝",
  call: "📞",
  meeting: "🗓",
  email: "✉",
  whatsapp: "💬",
  status_change: "→",
  assignment: "👤",
  transfer: "↔",
  distribution: "📤",
};

interface Props { activities: ActivityRow[] }

function describePayload(
  row: ActivityRow,
  resolveName: (id: string | null | undefined) => string
): string | null {
  if (!row.payload || typeof row.payload !== "object") return null;
  const p = row.payload as Record<string, unknown>;
  if (row.type === "status_change" && p["from"] && p["to"]) {
    return `${String(p["from"])} → ${String(p["to"])}`;
  }
  if (row.type === "transfer" || row.type === "assignment") {
    const fromName = p["from"] ? resolveName(String(p["from"])) : null;
    const toName = p["to"] ? resolveName(String(p["to"])) : null;
    if (fromName && toName) return `${fromName} → ${toName}`;
    if (toName) return `Assigned to ${toName}`;
  }
  return null;
}

export function ActivityTimeline({ activities }: Props) {
  const { resolveName } = useMembersIndex();
  if (activities.length === 0) {
    return <p className="muted">No activity yet — log the first call or note above.</p>;
  }
  return (
    <ol className="timeline">
      {activities.map((a) => {
        const description = describePayload(a, resolveName);
        const actorName = a.actorUserId ? resolveName(a.actorUserId) : null;
        return (
          <li key={a.id} className="timeline-item">
            <span className="timeline-icon">{TYPE_ICONS[a.type] ?? "•"}</span>
            <div className="timeline-content">
              <div className="timeline-head">
                <span className="timeline-type">{a.type.replace("_", " ")}</span>
                {actorName ? <span className="muted">by {actorName}</span> : null}
                {a.outcome ? <span className="timeline-outcome">{a.outcome}</span> : null}
                <span className="timeline-time" title={a.occurredAt}>{formatRelative(a.occurredAt)}</span>
              </div>
              {description ? <div className="timeline-body">{description}</div> : null}
              {a.body ? <div className="timeline-body">{a.body}</div> : null}
            </div>
          </li>
        );
      })}
    </ol>
  );
}
